import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography } from '@mui/material';
import median from 'median-js-bridge';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import {
    dismissAppPrompt,
    getMobilePlatform,
    openAppDownload,
    shouldShowAppPrompt,
} from '../../../shared/appDeepLink';

const BANNER_HEIGHT_VAR = '--app-prompt-banner-height';

export default function OpenInAppPrompt() {
    const platform = getMobilePlatform(
        navigator.userAgent,
        navigator.maxTouchPoints,
    );
    const bannerRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(() =>
        shouldShowAppPrompt(navigator.userAgent, navigator.maxTouchPoints),
    );

    useEffect(() => {
        median.onReady(() => setOpen(false));
    }, []);

    useLayoutEffect(() => {
        const el = bannerRef.current;
        if (!open || !platform || !el) {
            document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
            return;
        }

        const apply = () => {
            document.documentElement.style.setProperty(
                BANNER_HEIGHT_VAR,
                `${el.offsetHeight}px`,
            );
        };
        apply();

        if (typeof ResizeObserver === 'undefined') {
            return () => {
                document.documentElement.style.removeProperty(
                    BANNER_HEIGHT_VAR,
                );
            };
        }

        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => {
            observer.disconnect();
            document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
        };
    }, [open, platform]);

    if (!open || !platform) return null;

    const onContinue = () => {
        dismissAppPrompt();
        setOpen(false);
    };

    const onOpenInApp = () => {
        openAppDownload(platform);
    };

    return (
        <Box
            ref={bannerRef}
            component='section'
            role='region'
            aria-label='Open in the 7DAY app'
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1200,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pl: 0.5,
                pr: 1.5,
                pb: 1,
                pt: 'max(8px, env(safe-area-inset-top, 0px))',
                bgcolor: 'background.paper',
                color: 'text.primary',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <IconButton
                aria-label='Continue on web'
                onClick={onContinue}
                size='small'
                sx={{ color: 'text.secondary' }}
            >
                <CloseIcon />
            </IconButton>
            <Box
                component='img'
                alt=''
                src='/illustrations/rocketicon.png'
                sx={{
                    height: 40,
                    width: 40,
                    objectFit: 'contain',
                    borderRadius: 1.5,
                    flexShrink: 0,
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    component='p'
                    variant='subtitle2'
                    sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}
                >
                    7DAY
                </Typography>
                <Typography
                    component='p'
                    variant='caption'
                    color='text.secondary'
                    noWrap
                    sx={{ m: 0 }}
                >
                    Open in the 7DAY app
                </Typography>
            </Box>
            <CustomButton
                onClick={onOpenInApp}
                variant='contained'
                color='secondary'
                size='small'
                sx={{ flexShrink: 0, minWidth: 72 }}
            >
                Open
            </CustomButton>
        </Box>
    );
}
